/**
 * Tiny Web-Audio one-shot SFX player that reuses the Unity client's active clips (public/sfx).
 * Buffers decode once on init; playback is gated by a persisted mute preference and the AudioContext
 * is resumed on the first user gesture (browser autoplay policy).
 */

export type SfxName =
  | 'ready'
  | 'blackjack-card'
  | 'blackjack-win'
  | 'blackjack-lose'
  | 'blackjack-bet-info'
  | 'blackjack-bet-warning'
  | 'hokm-card'
  | 'backgammon-dice'
  | 'backgammon-move';

export const SFX_FILES: Readonly<Record<SfxName, string>> = {
  ready: '/sfx/ready.mp3',
  'blackjack-card': '/sfx/blackjack-card.wav',
  'blackjack-win': '/sfx/blackjack-win.wav',
  'blackjack-lose': '/sfx/blackjack-lose.mp3',
  'blackjack-bet-info': '/sfx/blackjack-bet-info.mp3',
  'blackjack-bet-warning': '/sfx/blackjack-bet-warning.mp3',
  'hokm-card': '/sfx/hokm-card.wav',
  'backgammon-dice': '/sfx/backgammon-dice.mp3',
  'backgammon-move': '/sfx/backgammon-move.mp3',
};

export interface BlackjackAudioState {
  minBet: number;
  bet: number;
  player1Balance: bigint;
  player2Balance: bigint;
}

/** Mirrors Unity BlackjackUI.PlaySoundBasedOnBet, whose balance comparison uses the previous packet. */
export function blackjackSfxForState(previous: BlackjackAudioState | null, next: BlackjackAudioState): SfxName {
  if (!previous) return 'blackjack-card';

  const betChanged = next.bet !== previous.bet;
  if (betChanged && (BigInt(next.bet) === previous.player1Balance || BigInt(next.bet) === previous.player2Balance)) {
    return 'blackjack-bet-warning';
  }
  if (betChanged && next.bet !== next.minBet) return 'blackjack-bet-info';
  return 'blackjack-card';
}

/**
 * A normal Hokm play appends exactly one card to the current trick. Requiring the old deck as an
 * unchanged prefix keeps initial loads, refreshes, trick collection, and reconnect jumps silent.
 */
export function didHokmDeckAppendCard(
  previousDeck: readonly string[] | null,
  nextDeck: readonly string[],
): boolean {
  if (previousDeck === null || nextDeck.length !== previousDeck.length + 1) return false;
  return previousDeck.every((code, index) => nextDeck[index] === code);
}

/** Returns the per-card Hakem-deal volume once; repeated renders of the same card stay silent. */
export function hokmDealCardSfxVolume(
  playedIndices: Set<number>,
  cardIndex: number,
  isAce: boolean,
): number | null {
  if (playedIndices.has(cardIndex)) return null;
  playedIndices.add(cardIndex);
  return isAce ? 0.8 : 0.35;
}

const MUTE_KEY = 'sfxMuted';

let ctx: AudioContext | null = null;
const buffers = new Map<SfxName, AudioBuffer>();
let muted = false;
let started = false;

function readMutedPref(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Decode the SFX set and wire a one-time gesture unlock. Idempotent. */
export function initSound(): void {
  if (started) return;
  started = true;
  muted = readMutedPref();
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  for (const name of Object.keys(SFX_FILES) as SfxName[]) {
    void fetch(SFX_FILES[name])
      .then((r) => r.arrayBuffer())
      .then((b) => ctx!.decodeAudioData(b))
      .then((buf) => buffers.set(name, buf))
      .catch(() => {
        /* a missing/undecodable sound simply never plays */
      });
  }
  const unlock = (): void => {
    void ctx?.resume();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

/** Play a one-shot effect (no-op while muted, before init, or if the buffer is still loading). */
export function playSfx(name: SfxName, volume = 1): void {
  if (muted || !ctx) return;
  const buf = buffers.get(name);
  if (!buf) return;
  if (ctx.state === 'suspended') void ctx.resume();
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const gain = ctx.createGain();
  gain.gain.value = volume;
  src.connect(gain).connect(ctx.destination);
  src.start();
}

export function isSfxMuted(): boolean {
  return muted;
}

export function setSfxMuted(value: boolean): void {
  muted = value;
  try {
    localStorage.setItem(MUTE_KEY, value ? '1' : '0');
  } catch {
    /* persistence is best-effort */
  }
}
